import {
    ENTRY_TIMING_WEEKDAYS,
    type EntryTimingIntradayBucket,
    type EntryTimingProfile,
    type EntryTimingProfileCell,
    type EntryTimingProfileMetrics,
    type EntryTimingWeekday
} from '@einhaus/project314-types';

export type EntryTimingMetricKey =
    | 'meanReturnPercent'
    | 'medianReturnPercent'
    | 'winRatePercent'
    | 'meanAlphaPercent'
    | 'relativeEdgePercent'
    | 'meanMfePercent'
    | 'meanMaePercent'
    | 'acceptanceRatePercent'
    | 'optionTradableRatePercent'
    | 'entryCount';

export interface EntryTimingMetricDefinition {
    key: EntryTimingMetricKey;
    label: string;
    scale: 'diverging' | 'sequential' | 'count';
    format: 'percent' | 'count';
}

export interface EntryTimingCellEvidence {
    entrySharePercent: number;
    reasons: string[];
    thin: boolean;
}

export const ENTRY_TIMING_METRICS: readonly EntryTimingMetricDefinition[] = [
    { format: 'percent', key: 'meanReturnPercent', label: 'Mean return', scale: 'diverging' },
    { format: 'percent', key: 'medianReturnPercent', label: 'Median return', scale: 'diverging' },
    { format: 'percent', key: 'winRatePercent', label: 'Win rate', scale: 'sequential' },
    { format: 'percent', key: 'meanAlphaPercent', label: 'Mean alpha', scale: 'diverging' },
    { format: 'percent', key: 'relativeEdgePercent', label: 'Timing edge', scale: 'diverging' },
    { format: 'percent', key: 'meanMfePercent', label: 'Mean MFE', scale: 'diverging' },
    { format: 'percent', key: 'meanMaePercent', label: 'Mean MAE', scale: 'diverging' },
    { format: 'percent', key: 'acceptanceRatePercent', label: 'Acceptance rate', scale: 'sequential' },
    { format: 'percent', key: 'optionTradableRatePercent', label: 'Option tradability', scale: 'sequential' },
    { format: 'count', key: 'entryCount', label: 'Entry count', scale: 'count' }
];

const metricByKey = new Map(ENTRY_TIMING_METRICS.map((metric) => [metric.key, metric]));

export function getEntryTimingMetricDefinition(metricKey: EntryTimingMetricKey): EntryTimingMetricDefinition {
    const definition = metricByKey.get(metricKey);

    if (!definition) throw new Error(`Unknown entry timing metric: ${metricKey}`);

    return definition;
}

export function getEntryTimingMetricValue(metrics: EntryTimingProfileMetrics, metricKey: EntryTimingMetricKey): number | null {
    switch (metricKey) {
        case 'relativeEdgePercent':
            return metrics.relativeEdge?.meanPercent ?? null;
        case 'acceptanceRatePercent':
            return metrics.selection?.acceptanceRatePercent ?? null;
        case 'optionTradableRatePercent':
            return metrics.optionTradability?.tradableRatePercent ?? null;
        default:
            return metrics[metricKey];
    }
}

export function getAvailableEntryTimingMetrics(profile: EntryTimingProfile): EntryTimingMetricDefinition[] {
    return ENTRY_TIMING_METRICS.filter(
        (definition) =>
            definition.key === 'entryCount' ||
            getEntryTimingMetricValue(profile.summary, definition.key) !== null ||
            profile.cells.some((cell) => getEntryTimingMetricValue(cell.metrics, definition.key) !== null)
    );
}

export function selectDefaultEntryTimingMetric(profile: EntryTimingProfile): EntryTimingMetricKey {
    return getAvailableEntryTimingMetrics(profile)[0]?.key ?? 'entryCount';
}

export function formatEntryTimingMetricValue(value: number | null, metricKey: EntryTimingMetricKey): string {
    if (value === null) return '—';

    const definition = getEntryTimingMetricDefinition(metricKey);

    if (definition.format === 'count') return Math.round(value).toLocaleString('en-US');

    if (definition.scale === 'diverging') {
        return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
    }

    return `${value.toFixed(1)}%`;
}

function formatClockMinute(minuteOfDay: number): string {
    if (minuteOfDay === 1_440) return '24:00';

    const hours = Math.floor(minuteOfDay / 60);
    const minutes = minuteOfDay % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

function formatElapsedMinute(minute: number): string {
    const hours = Math.floor(minute / 60);
    const minutes = minute % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
}

export function formatEntryTimingBucketLabel(bucket: EntryTimingIntradayBucket, mode: 'wallClock' | 'sessionElapsed'): string {
    if (mode === 'wallClock') {
        return `${formatClockMinute(bucket.startMinute)}–${formatClockMinute(bucket.endMinute)}`;
    }

    return `Open +${formatElapsedMinute(bucket.startMinute)}–${formatElapsedMinute(bucket.endMinute)}`;
}

export function getEntryTimingWeekdays(profile: EntryTimingProfile): EntryTimingWeekday[] {
    const present = new Set(profile.cells.map((cell) => cell.weekday));
    return ENTRY_TIMING_WEEKDAYS.filter((weekday) => present.has(weekday));
}

export function getEntryTimingCell(
    profile: EntryTimingProfile,
    weekday: EntryTimingWeekday,
    intradayBucketKey: string | null
): EntryTimingProfileCell | null {
    return profile.cells.find((cell) => cell.weekday === weekday && cell.intradayBucketKey === intradayBucketKey) ?? null;
}

export function getEntryTimingCellEvidence(profile: EntryTimingProfile, cell: EntryTimingProfileCell): EntryTimingCellEvidence {
    const reasons: string[] = [];
    const { metrics } = cell;
    const { thresholds } = profile;

    if (metrics.entryCount < thresholds.minCellEntryCount) {
        reasons.push(`${metrics.entryCount} entries; ${thresholds.minCellEntryCount} required for full prominence`);
    }

    if (metrics.distinctDateCount < thresholds.minCellDistinctDateCount) {
        reasons.push(`${metrics.distinctDateCount} dates; ${thresholds.minCellDistinctDateCount} required`);
    }

    if (metrics.distinctEntityCount < thresholds.minCellDistinctEntityCount) {
        reasons.push(`${metrics.distinctEntityCount} entities; ${thresholds.minCellDistinctEntityCount} required`);
    }

    const entrySharePercent = profile.summary.entryCount === 0 ? 0 : (metrics.entryCount / profile.summary.entryCount) * 100;

    if (entrySharePercent > thresholds.maxCellEntrySharePercent) {
        reasons.push(`${entrySharePercent.toFixed(1)}% of entries; concentration limit is ${thresholds.maxCellEntrySharePercent}%`);
    }

    return { entrySharePercent, reasons, thin: reasons.length > 0 };
}

export function getEntryTimingMetricScale(profile: EntryTimingProfile, metricKey: EntryTimingMetricKey): number {
    const definition = getEntryTimingMetricDefinition(metricKey);

    if (definition.scale === 'sequential') return 100;

    const values = profile.cells.flatMap((cell) => {
        const value = getEntryTimingMetricValue(cell.metrics, metricKey);
        return value === null ? [] : [Math.abs(value)];
    });

    return Math.max(0, ...values);
}

export function getEntryTimingCellStyle(
    profile: EntryTimingProfile,
    cell: EntryTimingProfileCell,
    metricKey: EntryTimingMetricKey,
    scale: number
): { backgroundColor?: string; opacity: number } {
    const value = getEntryTimingMetricValue(cell.metrics, metricKey);
    const definition = getEntryTimingMetricDefinition(metricKey);
    const evidence = getEntryTimingCellEvidence(profile, cell);
    const opacity = 1;

    if (value === null || scale <= 0) return { opacity };

    const intensity = Math.min(Math.abs(value) / scale, 1);
    const evidenceFactor = evidence.thin ? 0.4 : 1;
    const alpha = (0.08 + intensity * 0.38) * evidenceFactor;

    if (definition.scale === 'diverging') {
        return {
            backgroundColor: value >= 0 ? `rgba(34, 197, 94, ${alpha.toFixed(3)})` : `rgba(239, 68, 68, ${alpha.toFixed(3)})`,
            opacity
        };
    }

    return { backgroundColor: `rgba(59, 130, 246, ${alpha.toFixed(3)})`, opacity };
}

export function buildEntryTimingCellTitle(
    profile: EntryTimingProfile,
    cell: EntryTimingProfileCell,
    metricKey: EntryTimingMetricKey,
    bucket: EntryTimingIntradayBucket | null
): string {
    const definition = getEntryTimingMetricDefinition(metricKey);
    const mode = profile.semantics.intradayBinning.mode;
    const bucketLabel = bucket && mode !== 'none' ? formatEntryTimingBucketLabel(bucket, mode) : 'weekday total';
    const evidence = getEntryTimingCellEvidence(profile, cell);
    const lines = [
        `${cell.weekday} · ${bucketLabel}`,
        `${definition.label}: ${formatEntryTimingMetricValue(getEntryTimingMetricValue(cell.metrics, metricKey), metricKey)}`,
        `${cell.metrics.entryCount.toLocaleString('en-US')} entries · ${cell.metrics.distinctDateCount.toLocaleString('en-US')} dates · ${cell.metrics.distinctEntityCount.toLocaleString('en-US')} entities`
    ];

    if (evidence.reasons.length > 0) lines.push(`Thin evidence: ${evidence.reasons.join('; ')}`);

    return lines.join('\n');
}

export function formatEntryTimingSemantics(profile: EntryTimingProfile): string {
    const basis = profile.semantics.timestampBasis === 'decision' ? 'Decision time' : 'Execution time';
    const binning = profile.semantics.intradayBinning;
    const bucketLabel =
        binning.mode === 'none'
            ? 'weekday only'
            : `${binning.bucketMinutes}-minute ${binning.mode === 'sessionElapsed' ? 'session-elapsed' : 'wall-clock'} buckets`;

    return `${basis} · ${profile.semantics.timezone} · ${bucketLabel}`;
}
